import { PostDischargeProcedureType } from "../models/procedures.model";

/**
 * Pure helpers for the Care Transitions card/modal (KAN-2551).
 *
 * "Care Transitions" is a DISPLAY name only. Every identifier below — the
 * `POST_DISCHARGE`/`post-discharge` enum members, routes, query keys and i18n key
 * namespaces — is deliberately left alone, because `procedure.type` strings are
 * load-bearing for `user-state` (`last_discharge_timestamp`), the "New discharge"
 * task rule, the CHRISTUS campaign and two dbt views.
 */

/**
 * The shape the card needs from a procedure. Kept structural (rather than
 * `PostDischargeProcedure`) so both the `Procedure` rows the API returns and the
 * narrower post-discharge model satisfy it without a cast.
 */
export interface CareTransitionEvent {
	id: number;
	dateOn: Date | string;
	type: string;
	status: string;
}

/** How many events the quick-view card shows; the modal is the full history. */
export const CARE_TRANSITION_EVENT_LIMIT = 4;

/**
 * The types the discharge-derived summary (days since discharge, and `user-state`'s
 * `last_discharge_timestamp`) keys off. The cardiac-rehab types are deliberately NOT
 * here — a rehab event must never read as a hospital discharge.
 */
export const DISCHARGE_EVENT_TYPES: PostDischargeProcedureType[] = [
	PostDischargeProcedureType.DISCHARGED_FROM_HOSPITAL,
	PostDischargeProcedureType.ED_DISCHARGE,
];

/** Counterpart of {@link DISCHARGE_EVENT_TYPES} for `last_admission_timestamp`. */
export const ADMISSION_EVENT_TYPES: PostDischargeProcedureType[] = [
	PostDischargeProcedureType.HOSPITAL_ADMISSION,
	PostDischargeProcedureType.ED_ADMISSION,
];

/**
 * The cardiac-rehab event types, i.e. the second family on the card.
 * Kept separate from {@link DISCHARGE_EVENT_TYPES} / {@link ADMISSION_EVENT_TYPES} so a
 * rehab event can never be read as a hospital discharge.
 */
export const CARDIAC_REHAB_EVENT_TYPES: PostDischargeProcedureType[] = [
	PostDischargeProcedureType.CARDIAC_REHAB_START,
	PostDischargeProcedureType.CARDIAC_REHAB_COMPLETION,
];

/**
 * The families the card groups by, in display order: hospital/ED first, cardiac rehab
 * second (KAN-2551 follow-up — coaches read the card family by family, so interleaving
 * the two by date made it hard to scan).
 */
export const CARE_TRANSITION_FAMILY_ORDER = ["HOSPITAL_ED", "CARDIAC_REHAB"] as const;

export type CareTransitionFamily = (typeof CARE_TRANSITION_FAMILY_ORDER)[number];

export const careTransitionFamily = (type: string): CareTransitionFamily =>
	(CARDIAC_REHAB_EVENT_TYPES as string[]).includes(type) ? "CARDIAC_REHAB" : "HOSPITAL_ED";

/** Is this procedure type shown on the Care Transitions card/modal at all? */
export const isCareTransitionType = (type: string): boolean =>
	Object.keys(PostDischargeProcedureType).includes(type);

export const isDischargeEventType = (type: string): boolean =>
	(DISCHARGE_EVENT_TYPES as string[]).includes(type);

export const isAdmissionEventType = (type: string): boolean =>
	(ADMISSION_EVENT_TYPES as string[]).includes(type);

/** The Event Type dropdown options, in enum declaration order. */
export const careTransitionTypeOptions = (): { id: string; label: string }[] =>
	Object.keys(PostDischargeProcedureType).map((key) => ({
		id: key,
		label: `ENUMS.POST_DISCHARGE_PROCEDURES_TYPES.${key}`,
	}));

const byDateDescending = (a: CareTransitionEvent, b: CareTransitionEvent) =>
	new Date(b.dateOn).getTime() - new Date(a.dateOn).getTime();

/**
 * The event list rendered on the card.
 *
 * Selection is unchanged and stays GLOBAL: the `limit` most recent care-transition
 * events overall, so nothing that used to be visible is hidden by the grouping.
 * Only the *display order* is grouped — hospital/ED events first, then cardiac rehab,
 * newest-first inside each family (KAN-2551 follow-up).
 *
 * The modal remains the full, purely date-sorted history.
 */
export const toCareTransitionEvents = <T extends CareTransitionEvent>(
	procedures: T[] | undefined | null,
	limit: number = CARE_TRANSITION_EVENT_LIMIT
): T[] => {
	if (!procedures) {
		return [];
	}

	const mostRecent = [...procedures]
		.filter((procedure) => isCareTransitionType(procedure.type))
		.sort(byDateDescending)
		.slice(0, limit);

	return CARE_TRANSITION_FAMILY_ORDER.flatMap((family) =>
		mostRecent.filter((event) => careTransitionFamily(event.type) === family)
	);
};
